import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Phone, ChevronDown } from 'lucide-react';

const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'What We Do', path: '/what-we-do' },
  { name: 'Who We Serve', path: '/who-we-serve' },
  { name: 'Why Heritage', path: '/why-heritage' },
  { name: 'About', path: '/about' },
  { name: 'Home Care Guide', path: '/home-care-guide' },
  { name: 'Careers', path: '/careers' },
  { name: 'Contact', path: '/contact' },
];

export const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  return (
    <>
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? 'glass-strong shadow-glass py-2 sm:py-3'
            : 'bg-transparent py-3 sm:py-5'
        }`}
        data-testid="main-navigation"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2 group" data-testid="logo-link">
              <motion.div
                whileHover={{ scale: 1.02 }}
                className="flex items-start sm:items-center flex-col sm:flex-row sm:leading-none"
              >
                <span className="font-serif text-xl sm:text-2xl md:text-3xl font-semibold text-navy tracking-tight leading-none">
                  Heritage
                </span>
                <span className="font-serif text-base sm:text-2xl md:text-3xl font-light text-soft-gold sm:ml-2 leading-none mt-0.5 sm:mt-0">
                  Home Health
                </span>
              </motion.div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-1" data-testid="desktop-nav">
              {navLinks.slice(0, 6).map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-300 ${
                    location.pathname === link.path
                      ? 'bg-navy text-white'
                      : 'text-navy hover:bg-navy/5'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </nav>

            {/* Desktop CTA */}
            <div className="hidden lg:flex items-center space-x-4">
              <Link
                to="/careers"
                data-testid="nav-careers-btn"
                className="text-sm font-medium text-navy hover:text-soft-gold transition-colors"
              >
                Careers
              </Link>
              <Link
                to="/contact"
                data-testid="nav-contact-btn"
                className="flex items-center space-x-2 bg-navy text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-deep-blue transition-all duration-300 shadow-soft-3d hover:shadow-soft-3d-lg hover:-translate-y-0.5"
              >
                <Phone className="w-4 h-4" />
                <span>Contact Us</span>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(true)}
              className="lg:hidden p-2.5 rounded-xl hover:bg-navy/5 transition-colors"
              data-testid="mobile-menu-toggle"
              aria-label="Open menu"
            >
              <Menu className="w-6 h-6 text-navy" />
            </button>
          </div>
        </div>
      </motion.header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] lg:hidden"
            data-testid="mobile-menu"
          >
            <div
              className="absolute inset-0 bg-navy/20 backdrop-blur-sm"
              onClick={() => setIsMobileMenuOpen(false)}
            />
            <motion.nav
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="absolute top-0 right-0 bottom-0 w-full max-w-xs sm:max-w-sm bg-white shadow-2xl"
            >
              <div className="h-full overflow-y-auto p-5 sm:p-6 pb-10">
                <div className="flex items-center justify-between mb-8">
                  <span className="font-serif text-xl font-semibold text-navy">Menu</span>
                  <button
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="p-2 rounded-xl hover:bg-navy/5 transition-colors z-10"
                    data-testid="mobile-menu-close-button"
                  >
                    <X className="w-5 h-5 text-navy" />
                  </button>
                </div>
                <div className="space-y-2">
                  {navLinks.map((link, index) => (
                    <motion.div
                      key={link.path}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <Link
                        to={link.path}
                        data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                        className={`block px-4 py-3 rounded-xl text-base font-medium transition-all duration-300 ${
                          location.pathname === link.path
                            ? 'bg-navy text-white'
                            : 'text-navy hover:bg-navy/5'
                        }`}
                      >
                        {link.name}
                      </Link>
                    </motion.div>
                  ))}
                </div>
                <div className="mt-8 pt-8 border-t border-soft-gray">
                  <Link
                    to="/contact"
                    className="flex items-center justify-center space-x-2 bg-soft-gold text-navy px-6 py-4 rounded-full text-base font-semibold hover:bg-opacity-90 transition-all w-full"
                  >
                    <Phone className="w-5 h-5" />
                    <span>Call Us Now</span>
                  </Link>
                  <p className="text-center text-sm text-gray-500 mt-4">
                    +1 (608) 358-9578
                  </p>
                </div>
              </div>
            </motion.nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;
